/**
 * ROI Metrics & Substantiation Types
 * Defines typed schemas for ROI metric data and substantiation links
 * Task: 112-121-T001
 */

/**
 * Single ROI metric with quantified value, unit, and icon
 * Required: label, value, unit, icon
 * Optional: description for additional context
 */
export interface ROIMetric {
  label: string; // e.g., "Time Saved"
  value: number | string; // Numeric value (will be coerced to number for display)
  unit: string; // e.g., "hours/month", "%", "×"
  icon: string; // Heroicon name without 'Icon' suffix, e.g. "clock", "lightning-bolt"
  description?: string; // Optional additional context (e.g., "per team member")
}

/**
 * Substantiation link with validation for internal/external handling
 * Required: title, url
 * Optional: type (defaults to "external")
 */
export interface SubstantiationLink {
  title: string; // Display text for link (e.g., "ROI Case Study")
  url: string; // Absolute or relative URL; helper normalizes and sets target/rel
  type?: "internal" | "external"; // Defaults to "external"; controls target/rel behavior
}

/**
 * Complete ROI metrics block for a product
 * Contains metrics array and optional substantiation links
 */
export interface ROIMetricsBlock {
  metrics: ROIMetric[];
  substantiation?: SubstantiationLink[];
}

/**
 * Validation result for frontmatter mapping
 * Indicates success/failure and any issues encountered
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Default values for optional/missing fields
 */
export const ROI_DEFAULTS = {
  ICON_FALLBACK: "chart-bar", // Heroicon fallback when icon not found
  PLACEHOLDER_METRIC: {
    label: "Metric",
    value: "--",
    unit: "N/A",
    icon: "chart-bar",
    description: "Data unavailable",
  } as ROIMetric,
  LINK_TYPE_DEFAULT: "external" as const,
};

/**
 * Helper to normalize and validate a ROI metric from frontmatter
 * Applies defaults, validates required fields, and coerces types
 *
 * @param metric - Raw metric object from frontmatter
 * @param validation - Validation result object to populate with errors/warnings
 * @returns Normalized ROIMetric or placeholder if invalid
 */
export function normalizeMetric(
  metric: any,
  validation: ValidationResult
): ROIMetric {
  // Check required fields
  if (!metric?.label) {
    validation.errors.push("Metric missing required field: label");
    return ROI_DEFAULTS.PLACEHOLDER_METRIC;
  }

  if (metric.value === undefined || metric.value === null || metric.value === "") {
    validation.errors.push(`Metric "${metric.label}" missing required field: value`);
    return ROI_DEFAULTS.PLACEHOLDER_METRIC;
  }

  if (!metric.unit) {
    validation.errors.push(`Metric "${metric.label}" missing required field: unit`);
    return ROI_DEFAULTS.PLACEHOLDER_METRIC;
  }

  if (!metric.icon) {
    validation.warnings.push(`Metric "${metric.label}" missing icon; using fallback`);
  }

  // Coerce value to number if it's a pure numeric string
  let numValue: number | string = metric.value;
  if (typeof metric.value === "string") {
    // Only convert if the entire string is numeric (no text), e.g. "42" or "42.5" but not "3x faster"
    const trimmed = metric.value.trim();
    if (/^-?\d+\.?\d*$/.test(trimmed)) {
      const parsed = parseFloat(trimmed);
      if (!isNaN(parsed)) {
        numValue = parsed;
      }
    } else {
      numValue = metric.value; // Keep as string if contains text
    }
  }

  return {
    label: String(metric.label).trim(),
    value: numValue,
    unit: String(metric.unit).trim(),
    icon: metric.icon ? String(metric.icon).trim() : ROI_DEFAULTS.ICON_FALLBACK,
    description: metric.description ? String(metric.description).trim() : undefined,
  };
}

/**
 * Helper to normalize and validate a substantiation link
 * Validates URL format, determines target/rel based on internal/external
 *
 * @param link - Raw link object from frontmatter
 * @param validation - Validation result object to populate with errors/warnings
 * @returns Normalized SubstantiationLink or null if invalid
 */
export function normalizeLink(
  link: any,
  validation: ValidationResult
): SubstantiationLink | null {
  if (!link?.title) {
    validation.errors.push("Substantiation link missing required field: title");
    return null;
  }

  if (!link?.url) {
    validation.errors.push(`Link "${link.title}" missing required field: url`);
    return null;
  }

  const url = String(link.url).trim();
  const type = (link.type || ROI_DEFAULTS.LINK_TYPE_DEFAULT) as "internal" | "external";

  // Basic URL validation
  if (!url.startsWith("/") && !url.startsWith("http")) {
    validation.warnings.push(
      `Link "${link.title}" URL may be malformed; should start with / or http`
    );
  }

  return {
    title: String(link.title).trim(),
    url,
    type,
  };
}

/**
 * Helper to validate and normalize entire ROI metrics block
 * Processes all metrics and links, applies defaults, collects validation issues
 *
 * @param data - Raw ROI block data from frontmatter
 * @returns Normalized ROIMetricsBlock and validation result
 */
export function normalizeROIMetricsBlock(
  data: any
): { block: ROIMetricsBlock; validation: ValidationResult } {
  const validation: ValidationResult = {
    isValid: true,
    errors: [],
    warnings: [],
  };

  // Ensure metrics array exists
  if (!Array.isArray(data?.metrics)) {
    validation.errors.push("ROI block missing required field: metrics array");
    validation.isValid = false;
    return {
      block: { metrics: [ROI_DEFAULTS.PLACEHOLDER_METRIC] },
      validation,
    };
  }

  // Normalize metrics
  const metrics = data.metrics
    .map((m: any) => normalizeMetric(m, validation))
    .filter(Boolean); // Remove nulls (should not happen, but safe guard)

  if (metrics.length === 0) {
    validation.errors.push("ROI block has no valid metrics after normalization");
    validation.isValid = false;
    metrics.push(ROI_DEFAULTS.PLACEHOLDER_METRIC);
  } else {
    // At least one valid metric exists - consider valid even if there were per-metric errors
    validation.isValid = true;
  }

  // Normalize substantiation links (optional)
  const substantiation = Array.isArray(data.substantiation)
    ? data.substantiation
        .map((link: any) => normalizeLink(link, validation))
        .filter((normalized: SubstantiationLink | null): normalized is SubstantiationLink => normalized !== null)
    : undefined;

  return {
    block: { metrics, ...(substantiation && { substantiation }) },
    validation,
  };
}

/**
 * Helper to determine link target and rel attributes based on type
 * External links open in new tab with security headers; internal links stay in window
 *
 * @param link - SubstantiationLink
 * @returns Object with target and rel for use in HTML rendering
 */
export function getLinkAttributes(
  link: SubstantiationLink
): { target?: string; rel?: string } {
  if (link.type === "internal") {
    return {}; // Same window/tab, no target/rel needed
  }

  // External: open in new tab with security headers
  return {
    target: "_blank",
    rel: "noopener noreferrer",
  };
}
